/**
 * STARTING — countdown to game start.
 *
 * UI surface:
 *   - countdown overlay visible, "GAME STARTS IN N" label
 *   - profile cards in shooting mode (HI_LO doesn't enter until next state)
 *   - camera at default anchor
 *   - no betting / instruction / lock-in
 *
 * The server broadcasts every second with fresh `timeRemaining`. We
 * re-feed `start(remainingMs, label)` on each onUpdate; the overlay
 * resync's its ticker (keeps drift bounded to one broadcast cycle)
 * and updates the visible integer in place.
 *
 * onExit fires the pre-round dice cleanup so practice dice from
 * WAITING vanish before HI_LO / SHOOTER_SET_STACK takes over. Result
 * labels on opponents and the lock-in button get hidden defensively
 * — neither should be active here, but a stale flag from a prior
 * round shouldn't bleed into the next.
 */

import type { GameUpdate } from '@sdl/shared';
import { State } from '../State.js';

const COUNTDOWN_LABEL = 'GAME STARTS IN';

export class StartingState extends State {
  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;

    game.instructionBanner?.hide();
    game.bettingHUD3D?.setLockInVisible(false);
    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('shooting');
    game.cameraController?.setMode('default');

    const remainingMs = typeof update.timeRemaining === 'number' ? update.timeRemaining : 5000;
    game.countdownOverlay?.start(remainingMs, COUNTDOWN_LABEL);

    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    // Server broadcasts ~once per second; resync the overlay so drift
    // stays bounded to one broadcast cycle.
    const remainingMs = typeof update.timeRemaining === 'number' ? update.timeRemaining : 5000;
    this.game.countdownOverlay?.start(remainingMs, COUNTDOWN_LABEL);

    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onExit(_update: GameUpdate): Promise<void> {
    const game = this.game;
    game.countdownOverlay?.stop();

    // Pre-round cleanup. Practice dice from WAITING get cleared; any
    // lingering opponent result labels and the lock-in chip hide so
    // the next phase (HI_LO or SHOOTER_SET_STACK) starts clean.
    game.diceController?.clearDice();
    game.cameraController?.setDice([]);
    game.opponentDiceManager?.resetAllOpponents();
    game.opponentDiceManager?.hideAllResultLabels();
    game.bettingHUD3D?.setLockInVisible(false);
  }
}
